// Общее у карточек результата. SMY-114
//
// Карточки живут В ЛЕНТЕ, а не на своих экранах: вопрос, уточнение, ход
// работы и результат — одна хронология одного разговора. Отсюда и правило
// этой папки: карточка рисует ТОЛЬКО себя. Ни оболочки, ни заголовка ленты,
// ни поля ввода — всё это принадлежит чату, и три копии одного поля ввода
// разъезжались бы при первой же правке.
//
// Про деньги карточка тоже больше не говорит (06.09.2026). Списание стоит
// строкой НАД ней, окно бесплатной правки — ПОД ПОЛЕМ ВВОДА, где эту правку и
// набирают. Обе строки жили внутри карточки и повторяли друг друга: одна и та
// же мысль в трёх местах подряд.

import { useState, type ReactNode } from 'react'
import type { Artifact, UnitId } from '@smyslika/core'

import { Caps, Icon } from '../ui/index.ts'
import { Details } from '../screens/Details.tsx'

/** Что карточка знает о запуске, которым её выдали. */
export interface ResultMeta {
  /** Запуск: по нему открываются детали результата. */
  runId?: string
  /** Списалась ли единица. Экран говорит об этом прямо, а не в истории. */
  charged: boolean
  /** Чем списалось. Нужно, чтобы назвать списание словом человека. */
  unit: UnitId
}

export interface CardProps extends ResultMeta {
  artifact: Artifact
}

/**
 * Шапка карточки: что это, когда сделано и кнопка «ещё».
 *
 * Заголовок ленты для этого не годится: в ленте карточек несколько, и одна
 * шапка на весь экран назвала бы верхнюю или последнюю, но не ту, на которую
 * человек смотрит.
 */
export function CardHead({ title, meta, runId, right, info }: {
  title: string
  meta?: ReactNode
  runId?: string
  right?: ReactNode
  /**
   * Мелкий шрифт под значком «i»: пометки закона, границы ответственности.
   *
   * За значком, а не строкой в карточке: такой текст читают один раз и потом
   * пролистывают, а места он занимает как абзац ответа. Спрятать совсем
   * нельзя — он часть того, что мы обязаны сказать.
   */
  info?: ReactNode
}) {
  const [details, setDetails] = useState(false)
  const [note, setNote] = useState(false)

  return (
    <div className="rcard__head">
      <span className="rcard__title">
        <span className="rcard__name">{title}</span>
        {meta != null ? <Caps>{meta}</Caps> : null}
      </span>
      <span className="rcard__head-right">
        {right}
        {info != null ? (
          <span className="rcard__info" onMouseLeave={() => setNote(false)}>
            <button
              className="feed-header__more"
              aria-label="Что нужно знать про этот результат"
              aria-expanded={note}
              onMouseEnter={() => setNote(true)}
              onClick={() => setNote((v) => !v)}
            >
              <Icon name="info" size={18} />
            </button>
            {note ? <span className="rcard__info-pop">{info}</span> : null}
          </span>
        ) : null}
        {runId != null ? (
          <>
            <button className="feed-header__more" aria-label="Детали результата" onClick={() => setDetails(true)}>
              <Icon name="more" size={18} />
            </button>
            <Details runId={runId} open={details} onClose={() => setDetails(false)} />
          </>
        ) : null}
      </span>
    </div>
  )
}



/**
 * Скачать файл по подписанной ссылке.
 *
 * Открывается новой вкладкой, а не `<a download>`: ссылка ведёт в объектное
 * хранилище на другой домен, и атрибут `download` кросс-доменную ссылку молча
 * игнорирует — кнопка «Скачать» открыла бы картинку вместо файла и никак об
 * этом не сказала.
 */
export const openFile = (url: string): void => {
  window.open(url, '_blank', 'noreferrer')
}

/** Сообщение под кнопками карточки: «скопировано», «не выгрузилось». */
export function CardNote({ note }: { note: string | null }) {
  if (note == null) return null
  return (
    <p className="result__note" role="status">
      {note}
    </p>
  )
}
